// videoRecordingConfig.js

// Native MediaRecorder implementation for iOS devices
export class NativeMediaRecorder {
  constructor(stream, options) {
    this.stream = stream;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.options = options;
    this.isRecording = false;
  }

  startRecording() {
    this.recordedChunks = [];
    this.isRecording = true;

    // Check for supported MIME types on iOS
    const supportedTypes = [
      'video/mp4',
      'video/webm;codecs=h264',
      'video/webm;codecs=vp9',
      'video/webm',
      'video/quicktime'
    ];

    let mimeType = supportedTypes.find(type => {
      try {
        return MediaRecorder.isTypeSupported(type);
      } catch (e) {
        return false;
      }
    });

    if (!mimeType) {
      mimeType = 'video/webm'; // fallback
    }

    console.log(`Using MIME type: ${mimeType} for iOS recording`);

    const options = {
      mimeType,
      videoBitsPerSecond: this.options.videoBitsPerSecond || 400000,
      audioBitsPerSecond: this.options.audioBitsPerSecond || 128000
    };

    try {
      this.mediaRecorder = new MediaRecorder(this.stream, options);
    } catch (error) {
      // Fallback without bitrate constraints if not supported
      console.warn('Bitrate constraints not supported, using basic options:', error);
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
        console.log(`Data chunk collected: ${event.data.size} bytes`);
      }
    };

    this.mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event.error);
      this.isRecording = false;
    };

    this.mediaRecorder.onstart = () => {
      console.log('iOS MediaRecorder started successfully');
    };

    this.mediaRecorder.onstop = () => {
      console.log(`iOS MediaRecorder stopped. Total chunks: ${this.recordedChunks.length}`);
      this.isRecording = false;
    };

    // Start recording with data collection every second
    this.mediaRecorder.start(1000);
  }

  stopRecording(callback) {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.onstop = () => {
        // Give iOS some time to finalize the recording
        setTimeout(() => {
          console.log(`Final blob will have ${this.recordedChunks.length} chunks`);
          callback();
        }, 200);
      };

      if (this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop();
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  getBlob() {
    if (this.recordedChunks.length === 0) {
      console.warn('No recorded chunks available for blob creation');
      return new Blob([], { type: 'video/mp4' });
    }

    const mimeType = this.mediaRecorder ? this.mediaRecorder.mimeType : 'video/mp4';
    const blob = new Blob(this.recordedChunks, { type: mimeType });

    console.log(`Created blob: ${blob.size} bytes, type: ${blob.type}`);
    return blob;
  }

  // Method to check if recording is active
  isActivelyRecording() {
    return this.isRecording && this.mediaRecorder && this.mediaRecorder.state === 'recording';
  }
}

export const getDeviceConfig = () => {
  const userAgent = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const _isChrome = /Chrome/i.test(userAgent) && !/CriOS/i.test(userAgent);
  const _isChromeIOS = /CriOS/i.test(userAgent);
  const _isDesktop = !isIOS && !isAndroid;

  // Helper function to detect supported MIME types
  const getSupportedMimeType = () => {
    const types = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm;codecs=h264',
      'video/mp4'
    ];

    for (const type of types) {
      try {
        if (MediaRecorder.isTypeSupported(type)) {
          return type;
        }
      } catch (e) {
        continue;
      }
    }
    return 'video/webm'; // fallback
  };

  const _baseConfig = {
    video: {
      width: { ideal: 640 },
      height: { ideal: 480 },
      facingMode: "user"
    },
    audio: true
  };

  if (isIOS) {
    return {
      deviceType: 'iOS',
      useNativeRecorder: true, // Flag to use native MediaRecorder instead of RecordRTC
      mediaConstraints: {
        video: {
          width: { ideal: 640, max: 640 },
          height: { ideal: 480, max: 480 },
          facingMode: "user",
          frameRate: { ideal: 15, max: 30 }
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100
        }
      },
      nativeRecorderOptions: {
        videoBitsPerSecond: 400000,
        audioBitsPerSecond: 128000
      },
      recordRTCOptions: {
        type: "video",
        mimeType: "video/webm;codecs=H264",
        videoBitsPerSecond: 512000,
        frameInterval: 90,
        timeSlice: 1000,
        checkForInactiveTracks: true,
        bufferSize: 16384,
        ondataavailable: (blob) => {
          console.log("RecordRTC data available:", blob.size);
        }
      },
      stopStreamMethod: 'immediate',
      requiresManualStreamStop: true,
      videoPlaysinline: true
    };
  } else if (isAndroid) {
    const mimeType = getSupportedMimeType();
    console.log(`Android device using MIME type: ${mimeType}`);

    return {
      deviceType: 'Android',
      useNativeRecorder: false,
      mediaConstraints: {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user"
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      },
      recordRTCOptions: {
        type: "video",
        mimeType: mimeType,
        videoBitsPerSecond: 1024000, // 1 Mbps for better quality
        audioBitsPerSecond: 128000,
        frameInterval: 90,
        timeSlice: 1000, // Collect data every second
        checkForInactiveTracks: false,
        ondataavailable: (blob) => {
          console.log("RecordRTC data available:", blob.size);
        }
      },
      stopStreamMethod: 'graceful',
      requiresManualStreamStop: false,
      videoPlaysinline: true
    };
  } else {
    // Desktop configuration
    const mimeType = getSupportedMimeType();
    console.log(`Desktop device using MIME type: ${mimeType}`);

    return {
      deviceType: 'Desktop',
      useNativeRecorder: false,
      mediaConstraints: {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user"
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      },
      recordRTCOptions: {
        type: "video",
        mimeType: mimeType,
        videoBitsPerSecond: 1024000, // 1 Mbps for better quality
        audioBitsPerSecond: 128000,
        frameInterval: 90,
        timeSlice: 1000, // Collect data every second
        checkForInactiveTracks: false,
        ondataavailable: (blob) => {
          console.log("RecordRTC data available:", blob.size);
        }
      },
      stopStreamMethod: 'graceful',
      requiresManualStreamStop: false,
      videoPlaysinline: false
    };
  }
};

// Enhanced helper function to properly stop all media streams
export const stopAllMediaStreams = (stream, config) => {
  if (!stream) return;

  try {
    const tracks = stream.getTracks();

    if (config && config.deviceType === 'iOS') {
      // More aggressive cleanup for iOS
      tracks.forEach(track => {
        track.enabled = false;
        track.stop();
        console.log(`Stopped iOS ${track.kind} track, state: ${track.readyState}`);
      });

      // Multiple cleanup attempts for iOS Chrome with increasing delays
      const cleanupAttempts = [100, 300, 500, 1000];
      cleanupAttempts.forEach((delay, index) => {
        setTimeout(() => {
          if (stream.active) {
            const activeTracks = stream.getTracks().filter(track => track.readyState !== 'ended');
            if (activeTracks.length > 0) {
              console.log(`iOS cleanup attempt ${index + 1}: ${activeTracks.length} tracks still active`);
              activeTracks.forEach(track => {
                try {
                  track.stop();
                  console.log(`Force stopped ${track.kind} track on attempt ${index + 1}`);
                } catch (e) {
                  console.warn(`Failed to stop ${track.kind} track on attempt ${index + 1}:`, e);
                }
              });
            }
          }
        }, delay);
      });
    } else {
      // Standard cleanup for other devices
      tracks.forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track`);
      });
    }
  } catch (error) {
    console.error('Error stopping media streams:', error);
  }
};

// Helper function to check media device support
export const checkMediaDeviceSupport = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasVideoInput = devices.some(device => device.kind === 'videoinput');
    const hasAudioInput = devices.some(device => device.kind === 'audioinput');

    console.log('Media device support check:', { hasVideoInput, hasAudioInput, deviceCount: devices.length });

    return {
      hasVideoInput,
      hasAudioInput,
      devices
    };
  } catch (error) {
    console.error('Error checking media devices:', error);
    return {
      hasVideoInput: false,
      hasAudioInput: false,
      devices: []
    };
  }
};

// Helper function to validate MediaRecorder support
export const validateMediaRecorderSupport = () => {
  if (!window.MediaRecorder) {
    console.error('MediaRecorder not supported');
    return false;
  }

  // Test common MIME types
  const testTypes = [
    'video/mp4',
    'video/webm',
    'video/webm;codecs=vp8',
    'video/webm;codecs=vp9',
    'video/webm;codecs=h264'
  ];

  const supportedTypes = testTypes.filter(type => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch (e) {
      return false;
    }
  });

  console.log('Supported MediaRecorder MIME types:', supportedTypes);
  return supportedTypes.length > 0;
};

// iOS-specific blob validation
export const validateiOSBlob = async (blob, retryCallback) => {
  if (!blob || blob.size === 0) {
    console.warn('Empty blob detected, attempting retry...');

    // Wait a moment and retry
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (retryCallback) {
      const retryBlob = retryCallback();
      if (retryBlob && retryBlob.size > 0) {
        console.log('Retry successful, blob size:', retryBlob.size);
        return retryBlob;
      }
    }

    throw new Error('Unable to create valid video blob');
  }

  // Additional validation for minimum blob size
  if (blob.size < 1024) { // Less than 1KB is likely corrupt
    throw new Error(`Video blob too small (${blob.size} bytes), likely corrupt`);
  }

  console.log('Blob validation passed:', { size: blob.size, type: blob.type });
  return blob;
};
